import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { ConfirmPopup } from "primereact/confirmpopup";

import type { ExerciseContentInput, LessonDetails } from "../../hooks/useLessonLoader";
import { useLessonLoader } from "../../hooks/useLessonLoader";
import LessonCoverPage from "../Lesson/LessonCoverPage";
import VideoLesson from "../Lesson/VideoLesson";

import { fetchExerciseDetailsByKey } from "sj-ui-toolkit/src/api";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { ContentFeedback, getContentClient } from "sj-ui-toolkit/src/client";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";
import { useToast } from "sj-ui-toolkit/src/hooks";

import type { VideoLessonDetails } from "../Lesson/VideoLesson/VideoProvider";

export default function ExercisePage() {
  // Enable flex layout for this page
  const { "*": contentKey } = useParams();
  const [exerciseContent, setExerciseContent] = useState<ExerciseContentInput | null>(null);
  const { lessonDetails, setLessonDetails } = useLessonLoader({ exerciseContent });
  const { showToast } = useToast();
  const { selectedTenant } = useUserContext();
  const navigate = useNavigate();

  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        const data = await fetchExerciseDetailsByKey(contentKey!);
        setExerciseContent(data as ExerciseContentInput);
      } catch (error) {
        console.error("Error fetching exercise details:", error);
      }
    };

    fetchExercise();
  }, []);

  useEffect(() => {
    mainRef.current?.focus();
  }, [lessonDetails?.showAfterSurvey]);

  const onLessonFinish = () => {
    setLessonDetails({ ...lessonDetails, showAfterSurvey: true });
  };

  const submitLessonFeedback = async (feedback: ContentFeedback) => {
    if ((feedback.feedbackText || feedback.rating) && selectedTenant) {
      try {
        const contentClient = getContentClient();
        await contentClient.submitContentFeedback({ ...feedback, contentKey, selectedTenant });
        navigate("/");
      } catch (error) {
        console.error("Error submitting feedback:", error);
        showToast({
          severity: "error",
          summary: "Feedback submission failed",
          detail: "There was an issue submitting your feedback. Please try again.",
          life: 5000,
        });
      }
    } else {
      navigate("/");
    }
  };

  if (!exerciseContent || !lessonDetails) {
    return (
      <div className="h-full">
        <FullScreenLoader />
      </div>
    );
  }

  if (lessonDetails.showAfterSurvey) {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <ConfirmPopup />
        <LessonCoverPage
          headerText="Exercise complete!"
          description={lessonDetails.description}
          lessonType={lessonDetails.type}
          title={lessonDetails.title}
          difficulty={lessonDetails.level}
          duration={lessonDetails.duration}
          isAfterLessonSurvey={true}
          onSubmit={submitLessonFeedback}
          hasNextLesson={false}
          isPathCompleted={false}
          displayKnowledgeRating={false}
        />
      </div>
    );
  }

  return (
    <VideoLesson
      lessonDetails={lessonDetails as LessonDetails as VideoLessonDetails}
      onLessonFinish={onLessonFinish}
      exerciseOnly={true}
    />
  );
}
